<script setup lang="ts">
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import SearchField from '@/components/filter/SearchField.vue';
import { JobConfigurationDto } from '@/api/generator';
import { JobOverviewFilterObject } from '../types';
import { computed } from 'vue';
import PtwResetFilterButton from '@/components/buttons/PtwResetFilterButton.vue';
import { useTranslation } from 'i18next-vue';
import { uniq, uniqBy } from 'lodash';
import { truthy } from '@/utils/helper';

const props = withDefaults(
  defineProps<{
    initialData?: JobConfigurationDto[];
  }>(),
  {
    initialData: () => [],
  },
);

const options = computed(() => {
  const o = {
    targetSystems: uniqBy(
      props.initialData.map((jobConfiguration) => jobConfiguration.targetSystem).filter(truthy),
      (targetSystem) => targetSystem?.id,
    ).map((targetSystem) => ({
      title: targetSystem.name || '',
      value: targetSystem.id,
    })),

    protocols: uniqBy(
      props.initialData.flatMap((jobConfiguration) => jobConfiguration.protocols || []),
      (protocol) => protocol.id,
    ).map((protocol) => ({
      title: protocol.name,
      value: protocol.id,
    })),

    frequencies: uniq(
      props.initialData.map((jobConfiguration) => jobConfiguration.schedule?.frequency),
    )
      .filter(truthy)
      .map((freq) => ({
        title: t(`FrequencyType.${freq}`),
        value: freq,
      })),
  };

  return o;
});

const { t } = useTranslation();

const modelValue = defineModel<JobOverviewFilterObject>({ required: true });

function clearFilters() {
  modelValue.value = {};
}
</script>

<template>
  <v-row class="align-center mt-2 pl-2 pr-2">
    <v-col cols="12" sm="6" md="4" lg="3" class="pa-2">
      <SearchField v-model="modelValue.search" :placeholder="$t('Search')" />
    </v-col>

    <v-col cols="12" sm="6" md="4" lg="3" class="pa-2">
      <Autocomplete
        v-model="modelValue.targetSystemIds"
        :label="$t('MedicalSystem_other')"
        :items="options.targetSystems"
        multiple
        chips
        closableChips
        emptyAsUndefined
      ></Autocomplete>
    </v-col>

    <v-col cols="12" sm="6" md="4" lg="2" class="pa-2">
      <Autocomplete
        v-model="modelValue.frequency"
        :label="$t('Recurrence')"
        :items="options.frequencies"
        clearable
        emptyAsUndefined
      ></Autocomplete>
    </v-col>

    <v-col cols="12" sm="6" md="4" lg="2" class="pa-2">
      <Autocomplete
        v-model="modelValue.protocolIds"
        :label="$t('Protocol_other')"
        :items="options.protocols"
        multiple
        chips
        closableChips
        emptyAsUndefined
      ></Autocomplete>
    </v-col>

    <v-col cols="1" class="d-flex align-center justify-start pa-2">
      <PtwResetFilterButton @click="clearFilters" />
    </v-col>
  </v-row>
</template>
